import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './AuthContext';
import routes_web from './routes_web';
import { isTokenValidLocally } from '../offline_items/jwtUtils';

// ##### HELPER: Get cached user synchronously (same as AuthContext)
const getCachedUserSync = () => {
  try {
    const cached = localStorage.getItem('user_data_cache');
    if (cached) {
      const userData = JSON.parse(cached);
      const cacheTime = localStorage.getItem('user_data_cache_time');
      if (cacheTime && Date.now() - parseInt(cacheTime) < 24 * 60 * 60 * 1000) {
        return userData;
      }
    }
  } catch (error) {
    // Silent fail
  }
  return null;
};

const PrivateRoute = () => {
  const { user, get_user } = useAuth();
  
  // ##### OPTIMIZATION: Check if user exists immediately (from cache or context)
  // Verify both context user and localStorage cache to avoid unnecessary checks
  const cachedUser = getCachedUserSync();
  const accessToken = localStorage.getItem('access_token');
  const hasValidToken = accessToken && isTokenValidLocally(accessToken);
  const userExists = user || (cachedUser && hasValidToken);
  
  const [checkingAuth, setCheckingAuth] = useState(() => !userExists);

  useEffect(() => {
    // If user already exists (from cache or context), skip get_user() entirely
    if (user) {
      setCheckingAuth(false);
      return;
    }

    // Check cache as fallback before calling get_user()
    if (cachedUser && hasValidToken) {
      setCheckingAuth(false);
      // get_user() will be called by AuthContext useEffect, but we don't wait for it
      return;
    }

    // Only call get_user() if user doesn't exist and no valid cache
    const checkUser = async () => {
      await get_user();
      setCheckingAuth(false);
    };

    checkUser();
  }, [get_user]); // Remove 'user' from dependencies to avoid loops

  // Show a loader while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="border-4 border-gray-300 border-t-gray-700 rounded-full w-8 h-8 animate-spin"></div>
      </div>
    );
  }

  // If user is not authenticated, redirect to login
  return user ? <Outlet /> : <Navigate to={routes_web.accounts_login} replace />;
};

export default PrivateRoute;
